import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";

import { gql } from "@medbillai/generated-graphql-types";

import { errorHandling } from "../../../lib/apollo/utils";
import {
  normalizeClaimRecord,
  normalizeInvoiceRecord,
} from "../utils/healthDataBill";
import {
  claimStillOwed,
  collectionsNoticeItem,
  newestFirst,
  type RecordItem,
  statementItem,
  toRecordItem,
  withClaim,
  withInvoice,
} from "./records";
import { useRefetchOnRefocus } from "./useRefetchOnRefocus";

const getBillsClaimsClaimsQuery = gql(/* GraphQL */ `
  query GetBillsClaimsClaims {
    queryUserEobs {
      key
      kind
      data
      enrichment
      updatedAt
      linkedCase {
        id
        closed
        hasActions
        hasUnseenStaffMessage
        caseManagementClosureReason
        userClosureReason
        recordBillAmount
        negotiatedBillAmount
        billPaidAt
      }
    }
  }
`);

const getBillsClaimsBillsQuery = gql(/* GraphQL */ `
  query GetBillsClaimsBills {
    queryUserInvoices {
      key
      kind
      data
      enrichment
      updatedAt
      linkedCase {
        id
        closed
        hasActions
        hasUnseenStaffMessage
        caseManagementClosureReason
        userClosureReason
        recordBillAmount
        negotiatedBillAmount
        billPaidAt
      }
    }
  }
`);

const getBillsClaimsStatementsQuery = gql(/* GraphQL */ `
  query GetBillsClaimsStatements {
    queryUserDocuments(kind: STATEMENT) {
      key
      kind
      data
      enrichment
      updatedAt
      linkedCase {
        id
        closed
        hasActions
        hasUnseenStaffMessage
        caseManagementClosureReason
        userClosureReason
        recordBillAmount
        negotiatedBillAmount
        billPaidAt
      }
    }
  }
`);

const getBillsClaimsNoticesQuery = gql(/* GraphQL */ `
  query GetBillsClaimsNotices {
    queryUserDocuments(kind: COLLECTIONS_NOTICE) {
      key
      kind
      data
      enrichment
      updatedAt
      linkedCase {
        id
        closed
        hasActions
        hasUnseenStaffMessage
        caseManagementClosureReason
        userClosureReason
        recordBillAmount
        negotiatedBillAmount
        billPaidAt
      }
    }
  }
`);

// Per kind and error-tolerant, so one kind failing still lists the rest.
const options = {
  fetchPolicy: "cache-and-network",
  errorPolicy: "all",
  context: errorHandling("caller"),
} as const;

const recordQueries = [
  getBillsClaimsClaimsQuery,
  getBillsClaimsBillsQuery,
  getBillsClaimsStatementsQuery,
  getBillsClaimsNoticesQuery,
];

// A failed list shows its own error, so the refetch never rejects.
export const useRefetchRecords = () => {
  const client = useApolloClient();
  return useCallback(
    () =>
      client.refetchQueries({ include: recordQueries }).then(
        () => undefined,
        () => undefined,
      ),
    [client],
  );
};

export const useRecords = () => {
  const claimsQuery = useQuery(getBillsClaimsClaimsQuery, options);
  const billsQuery = useQuery(getBillsClaimsBillsQuery, options);
  const statementsQuery = useQuery(getBillsClaimsStatementsQuery, options);
  const noticesQuery = useQuery(getBillsClaimsNoticesQuery, options);
  const claimRows = claimsQuery.data?.queryUserEobs;
  const billRows = billsQuery.data?.queryUserInvoices;
  const statementRows = statementsQuery.data?.queryUserDocuments;
  const noticeRows = noticesQuery.data?.queryUserDocuments;

  const records = useMemo((): RecordItem[] => {
    const claims = (claimRows ?? []).flatMap(record => {
      const claim = normalizeClaimRecord(record);
      return claim
        ? [
            withClaim(
              toRecordItem(
                claim.row,
                "claim",
                record.linkedCase,
                claimStillOwed(claim.eob),
              ),
              claim.eob,
            ),
          ]
        : [];
    });
    const bills = (billRows ?? []).flatMap(record => {
      const bill = normalizeInvoiceRecord(record);
      return bill
        ? [
            withInvoice(
              toRecordItem(bill.row, "invoice", record.linkedCase),
              bill.invoice,
            ),
          ]
        : [];
    });
    const documents = [
      ...(statementRows ?? []).map(statementItem),
      ...(noticeRows ?? []).map(row => collectionsNoticeItem(row, new Date())),
    ].filter((item): item is RecordItem => item !== null);
    return newestFirst([...claims, ...bills, ...documents]);
  }, [claimRows, billRows, statementRows, noticeRows]);

  const queries = [claimsQuery, billsQuery, statementsQuery, noticesQuery];
  const refetch = useRefetchRecords();
  useRefetchOnRefocus(refetch);

  return {
    records,
    loading: queries.some(query => query.loading && !query.data),
    error: queries.find(query => query.error)?.error,
    /** A kind's list failed, so some records are missing. */
    incomplete: queries.some(query => Boolean(query.error) && !query.data),
    refetch,
  };
};
